import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'

import { useCallMutation } from '@/common/api/client'
import { apiPostFetcher, apiPutFetcher } from '@/common/api/fetcher'
import {
  CreateSiteResponse,
  CreateSiteVars,
  UpdateSiteResponse,
  UpdateSiteVars
} from '@/common/api/params/site'
import { ApiPath } from '@/common/api/path'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import { siteEditPath } from '@/common/app/path'
import { useShowSavedToast } from '@/common/app/toast'
import { registerNumberOption } from '@/common/form/register'
import { Site } from '@/common/model/site'
import { preventKeyDown } from '@/common/util/keyboard'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import { CheckBoxType } from '@/components/view/atoms/checkbox/CheckBox'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import { FieldType, TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import Content, { ContentType } from '@/components/view/layouts/page/Content'
import ContentSideFrame from '@/components/view/layouts/page/ContentSideFrame'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import Side from '@/components/view/layouts/page/Side'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import SiteFormApiInput from '@/components/container/form/modules/admin/site/SiteFormApiInput'
import SiteFormFeaturePageImage from '@/components/container/form/modules/admin/site/SiteFormFeaturePageImage'
import SiteFormFtpInput from '@/components/container/form/modules/admin/site/SiteFormFtpInput'
import {
  DEFAULT_SITE_SCHEMA,
  SiteFormSchemaType,
  convertSiteSchemaToVars,
  convertSiteToSchema,
  siteNameLabelMapping,
  siteSchema
} from '@/components/container/form/modules/admin/site/siteSchema'
import FormCheckBox from '@/components/container/form/widgets/FormCheckBox'
import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormRadioButton from '@/components/container/form/widgets/FormRadioButton'
import FormTextField, { FormTextFieldType } from '@/components/container/form/widgets/FormTextField'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'

interface Props {
  site?: Site
  onRefetch?(): void
}

const SiteForm = ({ site, onRefetch }: Props) => {
  const navigate = useNavigate()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty, isSubmitting },
    reset
  } = useForm<SiteFormSchemaType>({
    resolver: zodResolver(siteSchema),
    values: site ? convertSiteToSchema(site) : DEFAULT_SITE_SCHEMA,
    mode: 'onChange'
  })

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, siteNameLabelMapping)
  const showSaved = useShowSavedToast({ editing: !!site })

  const { trigger } = useCallMutation<
    CreateSiteResponse | UpdateSiteResponse,
    CreateSiteVars | UpdateSiteVars
  >(
    site ? ApiPath.UPDATE_SITE : ApiPath.CREATE_SITE,
    {
      onSuccess: (response: CreateSiteResponse | UpdateSiteResponse) => {
        showSaved(response.name)
        site && onRefetch ? onRefetch() : navigate(siteEditPath(response.id))
      }
    },
    site ? apiPutFetcher : apiPostFetcher
  )

  const onSubmit: SubmitHandler<SiteFormSchemaType> = (data) =>
    trigger(convertSiteSchemaToVars(data))

  // 表示条件は編集中の入力値に追従させる（保存を待たずに出し分ける）
  const enableContentAnalysis = useWatch({ control, name: 'enableContentAnalysis' })
  const w2Version = useWatch({ control, name: 'w2Version' })

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventKeyDown}>
      <PageTitle
        title={site ? site.name : 'サイトを新規作成する'}
        rightNode={
          <Button
            type='submit'
            size='large'
            color={ButtonColor.PRIMARY_BRAND}
            loading={isSubmitting}
          >
            {site ? '保存する' : '登録する'}
          </Button>
        }
      />
      {isDirty && (
        <TopBar
          status={site ? TopBarStatus.CHANGE : TopBarStatus.NEW}
          captionText={site ? '未反映の変更があります' : '登録されていない新しいサイト'}
          onCancel={() => reset(site ? convertSiteToSchema(site) : DEFAULT_SITE_SCHEMA)}
          loading={isSubmitting}
          withSide
        />
      )}
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />
      <ContentSideFrame>
        <Content type={site ? ContentType.TWO_COLUMNS_LEFT : ContentType.DEFAULT}>
          <Area noBorder>
            <Heading>基本情報</Heading>
            <AreaContent>
              <FormRow>
                <FormLabel required id='name'>
                  サイト名
                </FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormTextField
                    register={register('name')}
                    fieldType={FieldType.REQUIRED}
                    error={!!errors.name?.message}
                    size={TextBoxSize.LARGE}
                    placeholder='株式会社サンプル'
                  />
                  <FormError message={errors.name?.message} />
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel required id='domain'>
                  サイトURL
                </FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormTextField
                    register={register('domain')}
                    fieldType={FieldType.REQUIRED}
                    error={!!errors.domain?.message}
                    size={TextBoxSize.FULL}
                    placeholder='https://example.com'
                  />
                  <FormError message={errors.domain?.message} />
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel id='projectNumber' required>
                  プロジェクト番号
                </FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormTextField
                    register={register('projectNumber')}
                    error={!!errors.projectNumber?.message}
                    size={TextBoxSize.LARGE}
                    placeholder='PRJ-001'
                  />
                  <FormError message={errors.projectNumber?.message} />
                </FormRow>
              </FormRow>
              {site && (
                <>
                  <FormRow align='center'>
                    <FormLabel>マーチャントID（タグ埋込用）</FormLabel>
                    {site && <Text>{site.distribution_code}</Text>}
                  </FormRow>
                  <FormRow align='center'>
                    <FormLabel>APIキー</FormLabel>
                    {site && <Text>{site.api_key}</Text>}
                  </FormRow>
                </>
              )}
              <FormRow>
                <FormLabel>環境</FormLabel>
                <FormCheckBox
                  control={control}
                  name={'isProduction'}
                  type={CheckBoxType.TOGGLE_TEXT}
                  toggleText={{ left: '本番利用', right: 'テスト利用' }}
                />
              </FormRow>
              <FormRow>
                <FormLabel>利用Version</FormLabel>
                <FormRadioButton
                  control={control}
                  name={'w2Version'}
                  gap={12}
                  choices={[
                    { name: 'V5', value: 'v5' },
                    { name: 'V6', value: 'v6' }
                  ]}
                />
              </FormRow>
              {site && (
                <FormRow>
                  <FormLabel>ステータス</FormLabel>
                  <FormCheckBox
                    control={control}
                    name={'isActive'}
                    type={CheckBoxType.TOGGLE_TEXT}
                    toggleText={{ left: '稼働中', right: '非稼働' }}
                  />
                </FormRow>
              )}
            </AreaContent>
          </Area>
          <Area noBorder>
            <Heading>配信設定</Heading>
            <AreaContent>
              <FormRow>
                <FormLabel id='behaviorAnalysisLimit'>行動分析 最大取得件数</FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormRow gap={4} align='center'>
                    <FormTextField
                      register={register('behaviorAnalysisLimit', registerNumberOption)}
                      error={!!errors.behaviorAnalysisLimit?.message}
                      size={TextBoxSize.SMALL}
                      type={FormTextFieldType.NUMBER}
                    />
                    <Text>件（100〜10,000）</Text>
                  </FormRow>
                  <FormError message={errors.behaviorAnalysisLimit?.message} />
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel id='recSamplingRate'>レコメンド配信率</FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormRow gap={4} align='center'>
                    <FormTextField
                      register={register('recSamplingRate', registerNumberOption)}
                      error={!!errors.recSamplingRate?.message}
                      size={TextBoxSize.X_SMALL}
                      type={FormTextFieldType.NUMBER}
                    />
                    <Text>%（0=停止 / 100=全配信）</Text>
                  </FormRow>
                  <FormError message={errors.recSamplingRate?.message} />
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel>データ連動</FormLabel>
                <FormRow direction={'vertical'}>
                  {/* 連動先は W2Commerce のみ。項目ごと消すと連動先が画面から読めなくなるため、
                      選択済みの状態を非活性で残す */}
                  <FormRadioButton
                    control={control}
                    name={'recMasterMode'}
                    gap={12}
                    disabled
                    choices={[{ name: 'W2Commerce完全連動', value: 'w2_full' }]}
                  />
                </FormRow>
              </FormRow>
            </AreaContent>
          </Area>
          <Area noBorder>
            <Heading>機能設定</Heading>
            <AreaContent>
              <FormRow>
                <FormLabel>Reco-Media</FormLabel>
                <FormRow direction='vertical' gap={8}>
                  <FormCheckBox
                    control={control}
                    name={'enableRecoMedia'}
                    type={CheckBoxType.TOGGLE_TEXT}
                    toggleText={{ left: 'ON', right: 'OFF' }}
                  />
                  <Text color={TextColor.SECONDARY}>AIレコメンドを表示</Text>
                </FormRow>
              </FormRow>
              {/* 2026-10-06 追加: メールレコメンド（Reco-Media のオプション。openspec/changes/mail-recommend） */}
              <FormRow>
                <FormLabel>メールレコメンド</FormLabel>
                <FormRow direction='vertical' gap={8}>
                  <FormCheckBox
                    control={control}
                    name={'enableMailReco'}
                    type={CheckBoxType.TOGGLE_TEXT}
                    toggleText={{ left: 'ON', right: 'OFF' }}
                  />
                  <Text color={TextColor.SECONDARY}>
                    Reco-Media のオプション。メール本文に貼るレコメンドのコードを表示。OFFにするとメールの画像・リンクと日次の事前描画も止まります
                  </Text>
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel>Media-DRIVER</FormLabel>
                <FormRow direction='vertical' gap={8}>
                  <FormCheckBox
                    control={control}
                    name={'enableContentAnalysis'}
                    type={CheckBoxType.TOGGLE_TEXT}
                    toggleText={{ left: 'ON', right: 'OFF' }}
                  />
                  <Text color={TextColor.SECONDARY}>コンテンツ生成メニューを表示</Text>
                </FormRow>
              </FormRow>
              {/* V5区分はページのコンテンツと画像パスがマスタ連携に載らないため、
                  公開ページから画像を取得して補う。V6は連携で届くので設定を出さない */}
              {enableContentAnalysis && w2Version === 'v5' && (
                <SiteFormFeaturePageImage control={control} register={register} siteId={site?.id} />
              )}
              <FormRow>
                <FormLabel>記事アップロード</FormLabel>
                <FormRow direction='vertical' gap={8}>
                  <FormCheckBox
                    control={control}
                    name={'enableArticleUpload'}
                    type={CheckBoxType.TOGGLE_TEXT}
                    toggleText={{ left: 'ON', right: 'OFF' }}
                  />
                  <Text color={TextColor.SECONDARY}>Media-DRIVER - 記事アップロードを表示</Text>
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel>GEO/LLMO最適化</FormLabel>
                <FormRow direction='vertical' gap={8}>
                  <FormCheckBox
                    control={control}
                    name={'enableGeoLlmo'}
                    type={CheckBoxType.TOGGLE_TEXT}
                    toggleText={{ left: 'ON', right: 'OFF' }}
                  />
                  <Text color={TextColor.SECONDARY}>AI検索最適化メニューを表示</Text>
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel>UGC Intelligence</FormLabel>
                <FormRow direction='vertical' gap={8}>
                  <FormCheckBox
                    control={control}
                    name={'enableReview'}
                    type={CheckBoxType.TOGGLE_TEXT}
                    toggleText={{ left: 'ON', right: 'OFF' }}
                  />
                  <Text color={TextColor.SECONDARY}>レビュー/UGCメニューを表示</Text>
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel>Findee</FormLabel>
                <FormRow direction='vertical' gap={8}>
                  <FormCheckBox
                    control={control}
                    name={'enableAiPurchaseAssist'}
                    type={CheckBoxType.TOGGLE_TEXT}
                    toggleText={{ left: 'ON', right: 'OFF' }}
                  />
                  <Text color={TextColor.SECONDARY}>Findeeメニューを表示</Text>
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel>検索サジェスト</FormLabel>
                <FormRow direction='vertical' gap={8}>
                  <FormCheckBox
                    control={control}
                    name={'enableSearchSuggest'}
                    type={CheckBoxType.TOGGLE_TEXT}
                    toggleText={{ left: 'ON', right: 'OFF' }}
                  />
                  <Text color={TextColor.SECONDARY}>検索サジェスト（Findee）メニューを表示</Text>
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel>Tag Trail</FormLabel>
                <FormRow direction='vertical' gap={8}>
                  <FormCheckBox
                    control={control}
                    name={'enableTagPages'}
                    type={CheckBoxType.TOGGLE_TEXT}
                    toggleText={{ left: 'ON', right: 'OFF' }}
                  />
                  <Text color={TextColor.SECONDARY}>Tag Trail メニューを表示</Text>
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel>AIペルソナ</FormLabel>
                <FormRow direction='vertical' gap={8}>
                  <FormCheckBox
                    control={control}
                    name={'enablePersona'}
                    type={CheckBoxType.TOGGLE_TEXT}
                    toggleText={{ left: 'ON', right: 'OFF' }}
                  />
                  <Text color={TextColor.SECONDARY}>
                    AIペルソナメニューを表示。OFFにすると判定バッチと配信リスト同期も止まります
                  </Text>
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel>カルテ</FormLabel>
                <FormRow direction='vertical' gap={8}>
                  <FormCheckBox
                    control={control}
                    name={'enableCarte'}
                    type={CheckBoxType.TOGGLE_TEXT}
                    toggleText={{ left: 'ON', right: 'OFF' }}
                  />
                  <Text color={TextColor.SECONDARY}>データ分析メニューを表示</Text>
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel>顧客カルテオプション</FormLabel>
                <FormRow direction='vertical' gap={8}>
                  <FormCheckBox
                    control={control}
                    name={'enableFullBehavior'}
                    type={CheckBoxType.TOGGLE_TEXT}
                    toggleText={{ left: 'ON', right: 'OFF' }}
                  />
                  <Text color={TextColor.SECONDARY}>行動履歴を全取得（有料）</Text>
                </FormRow>
              </FormRow>
            </AreaContent>
          </Area>
        </Content>
        {site && (
          <Side>
            <SiteFormFtpInput
              site={site}
              onRefetch={onRefetch && (() => onRefetch())}
              isDirty={isDirty}
            />
            <SiteFormApiInput site={site} onRefetch={onRefetch && (() => onRefetch())} />
          </Side>
        )}
      </ContentSideFrame>
      <PageBottom>
        <Button type='submit' size='large' color={ButtonColor.PRIMARY_BRAND} loading={isSubmitting}>
          {site ? '保存する' : '登録する'}
        </Button>
      </PageBottom>
    </form>
  )
}

export default SiteForm
